import { Box } from "@mui/material"
import React, { useMemo } from "react"
import type { ImageI, SequenceI } from "tweenly"
import { getImageSrc, renderStyles } from "tweenly"
import { useDraggable } from "../../../../hooks/editor/useDraggable"
import useHoldDrag, { dragNodeRefKey } from "../../../../hooks/editor/useHoldDrag"
import useDynamicRefs from "../../../../hooks/useDynamicRefs"
import { useAppSelector } from "../../../../hooks/useRedux"
import {
    selectIsObjectSelected,
    selectSelectionCount,
} from "../../../../store/slices/activeObject.slice"
import { CanvasCursor } from "../../../../store/slices/canvasCursor.slice"
import { SELECTION_OVERLAY_REF_KEY } from "../SelectionOverlay"

interface Props {
    object: ImageI | SequenceI
    isHiding: boolean
    isLocking: boolean
    isRootTicker: boolean
    canvasScaleCoef: number
    borderCoef: number
    handleSelectObject: (
        event: React.MouseEvent,
        objectId: string,
        animationIds: string[],
        multiSelectKey: boolean
    ) => void
}

const ImageContainer = ({
    object,
    isHiding,
    isLocking,
    isRootTicker,
    canvasScaleCoef,
    handleSelectObject,
}: Props) => {
    const isSelected = useAppSelector((state) => selectIsObjectSelected(state, object.id))
    const selectionCount = useAppSelector(selectSelectionCount)
    const canvasCursor = useAppSelector((state) => state.canvasCursor.value)

    const objectStyles = renderStyles(object, "inline")
    const imageSrc = useMemo(() => getImageSrc(object, []), [object])

    const [getRef, setRef] = useDynamicRefs<HTMLImageElement>()

    const {
        handleOnStartDrag,
        handleDragAxisChange,
        handleDragEnd,
        getSnapSession,
        draggableScale,
        setCursorWhenDragging,
    } = useDraggable(object, canvasScaleCoef, undefined)
    const registerDragNode = setRef(dragNodeRefKey(object.id)) as React.RefCallback<HTMLDivElement>
    const visibility = object.isHidden || isHiding ? "hidden" : "visible"
    const isLocked = isLocking || object.isLocked

    // click selects, hold drags — same gesture as every other canvas object, see useHoldDrag
    useHoldDrag({
        enabled:
            isSelected &&
            selectionCount === 1 &&
            !isLocked &&
            !isRootTicker &&
            canvasCursor !== CanvasCursor.GrabCursorBySpace,
        scale: draggableScale,
        getDragNodes: () => [getRef(dragNodeRefKey(object.id))?.current ?? null],
        // outline out of the way while moving, so the image can be aligned by eye (GE-1649)
        getNodesToHide: () => [getRef(SELECTION_OVERLAY_REF_KEY)?.current ?? null],
        isHit: (stack) => stack.some((element) => element.id === object.id),
        onDragStart: handleOnStartDrag,
        onAxisChange: handleDragAxisChange,
        getSnapSession,
        onDragEnd: handleDragEnd,
    })

    const handleClick = (e: React.MouseEvent) => {
        handleSelectObject(e, object.id, Object.keys(object.animations), e.shiftKey)
    }

    return (
        <Box
            style={{
                cursor: "pointer",
                pointerEvents: isLocked ? "none" : "auto",
            }}
            onClick={(e) => {
                e.stopPropagation()
            }}
        >
            {/* `position: relative` keeps this node the containing block of the absolutely
                positioned image, which react-draggable used to get for free from its
                permanent `transform: translate(0,0)`. React owns `position`, the hold
                gesture owns `transform` — neither can undo the other. */}
            <Box ref={registerDragNode} style={{ position: "relative" }}>
                <img
                    src={imageSrc}
                    alt={object.id}
                    className={`image-container ${object.id}`}
                    id={object.id}
                    ref={setRef(object.id)}
                    onClick={handleClick}
                    style={{
                        ...objectStyles,
                        visibility: visibility,
                        // outline and resize handles are drawn by SelectionOverlay, above
                        // every object, so they survive being covered by another one
                        cursor: setCursorWhenDragging,
                    }}
                />
            </Box>
        </Box>
    )
}

export default ImageContainer
